import {Box, Button, Group, Modal, Stack, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {ElementMouseEvent} from 'util/Types';

import {TABLE_NAME_MAX_CHARS} from '../TableConstants';

const getError = (
  tableName: string,
  isTableNameUsed: (tableName: string) => boolean,
  initialTableName: string
): string | null => {
  if (tableName.length === 0) {
    return 'Table name cannot be empty';
  }
  if (tableName.length > TABLE_NAME_MAX_CHARS) {
    return `Table name exceeds ${TABLE_NAME_MAX_CHARS} characters`;
  }
  if (isTableNameUsed(tableName) && tableName !== initialTableName) {
    return 'Table name already in use';
  }
  return null;
};

const RENAME_TABLE_MODAL_LABEL_ID = 'RENAME_TABLE_LABEL';

export default function RenameTableModal({
  initialTableName = '',
  onTableRenamed,
  isTableNameUsed,
}: {
  initialTableName?: string;
  isTableNameUsed: (tableName: string) => boolean;
  onTableRenamed: (tableName: string | null) => void;
}): React.ReactElement {
  const [tableName, setTableName] = useState(initialTableName);

  const handleRenameTable = (): void => {
    if (getError(tableName, isTableNameUsed, initialTableName) != null) {
      return;
    }
    if (tableName === initialTableName) {
      onTableRenamed(null);
      return;
    }
    onTableRenamed(tableName);
  };

  const error = getError(tableName, isTableNameUsed, initialTableName);

  return (
    <div
      onMouseDown={(e: ElementMouseEvent) => {
        e.stopPropagation();
      }}
    >
      <Modal
        opened={true}
        title="Rename table"
        // Keep this modal in-tree: Enzyme teardown in TableEditable-test.tsx can throw
        // NotFoundError when Mantine portals mount under document.body.
        withinPortal={false}
        onClose={() => {
          onTableRenamed(null);
        }}
      >
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleRenameTable();
          }}
        >
          <Stack spacing="sm">
            <TextInput
              label="Table Name"
              id={RENAME_TABLE_MODAL_LABEL_ID}
              value={tableName}
              onChange={(e) => {
                setTableName(e.target.value);
              }}
              error={error}
              data-autofocus={true}
              onFocus={(e) => {
                e.currentTarget.select();
              }}
            />
            <Group position="right">
              <Button type="submit" disabled={error != null}>
                Save
              </Button>
            </Group>
          </Stack>
        </Box>
      </Modal>
    </div>
  );
}
